import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { articles } from '@/mocks/articles';
import Footer from '@/pages/home/components/Footer';

export default function GuideArticle() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vigorbuy.yachts';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center px-4">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-2xl text-foreground-500"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">Article Not Found</h1>
          <p className="text-sm text-foreground-600 mb-6">The article you are looking for does not exist or has been moved.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-label font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="text-xl font-heading font-bold text-foreground-950 tracking-tight whitespace-nowrap cursor-pointer"
            >
              Vigorbuy
            </Link>
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="px-4 py-2 rounded-lg text-sm font-label text-foreground-600 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Home
              </Link>
              <Link
                to="/vigorbuy-spreadsheet-guide"
                className="px-4 py-2 rounded-lg text-sm font-label text-foreground-600 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Shopping Guide
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Vigorbuy Sheet
              </a>
            </div>
          </div>
        </div>
      </nav>

      <main>
        {/* Article Header */}
        <header className="py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-label font-medium">
                Guide
              </span>
              <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-label font-medium">
                {article.readTime}
              </span>
            </div>

            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>

            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto">
              {article.excerpt}
            </p>
          </div>
        </header>

        {/* Article Content */}
        <article className="py-12 md:py-16 px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="space-y-8 md:space-y-10">
              {article.sections.map((section, i) => (
                <section key={i}>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((para, j) => (
                    <p
                      key={j}
                      className={`text-sm md:text-base text-foreground-700 leading-relaxed ${j < section.paragraphs.length - 1 ? 'mb-3' : ''}`}
                      dangerouslySetInnerHTML={{ __html: para }}
                    />
                  ))}
                </section>
              ))}

              {/* Final CTA */}
              <section className="text-center py-8 md:py-10 px-6 rounded-2xl bg-background-100 border border-background-200/70">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                  Ready to Try Vigorbuy Spreadsheet?
                </h2>
                <p className="text-sm md:text-base text-foreground-600 mb-6 leading-relaxed max-w-lg mx-auto">
                  Experience faster product comparisons and smarter shopping decisions. Start exploring the vigorbuy spreadsheet today.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-label font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                  >
                    Explore Vigorbuy Sheet
                  </a>
                  <Link
                    to="/"
                    className="w-full sm:w-auto px-8 py-3.5 rounded-full border-2 border-primary-500 text-primary-600 text-sm font-label font-semibold hover:bg-primary-50 transition-all cursor-pointer whitespace-nowrap"
                  >
                    Back to Home
                  </Link>
                </div>
              </section>

              {/* Related Articles */}
              <section>
                <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                  More Vigorbuy Spreadsheet Guides
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {articles
                    .filter((a) => a.slug !== article.slug)
                    .slice(0, 4)
                    .map((rel) => (
                      <Link
                        key={rel.slug}
                        to={`/guide/${rel.slug}`}
                        className="flex items-center gap-3 p-3 rounded-lg bg-background-100 border border-background-200/60 hover:border-primary-300 transition-colors cursor-pointer"
                      >
                        <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                          <i className={`${rel.icon} text-sm`}></i>
                        </span>
                        <span className="text-sm font-label font-medium text-foreground-800 line-clamp-2 leading-snug">
                          {rel.title}
                        </span>
                      </Link>
                    ))}
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>

      <Footer />

      {/* Schema.org Article Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            'headline': article.title,
            'description': article.metaDescription,
            'url': `${siteUrl}/guide/${article.slug}`,
            'datePublished': '2026-06-29',
            'dateModified': '2026-06-29',
            'author': {
              '@type': 'Organization',
              'name': 'Vigorbuy Spreadsheet',
              'url': siteUrl,
            },
            'publisher': {
              '@type': 'Organization',
              'name': 'Vigorbuy Spreadsheet',
              'url': siteUrl,
            },
            'mainEntityOfPage': {
              '@type': 'WebPage',
              '@id': `${siteUrl}/guide/${article.slug}`,
            },
          }),
        }}
      />
    </div>
  );
}